var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import '@styles/webdesktop/stylesheets/contextMenu.scss';
import React, { useCallback, useRef } from 'react';
import { useContextMenu } from './useContextMenu';
import { Popover } from '../Popover/Popover';
import { ContextMenuItem } from './ContextMenuItems/ContextMenuItem';
import { ContextMenuItemAccount } from './ContextMenuItems/ContextMenuItemAccount';
import { ContextMenuItemActions } from './ContextMenuItems/ContextMenuItemActions';
import { ContextMenuItemSeparator } from './ContextMenuItems/ContextMenuItemSeparator';
import { useAccountContextMenu } from './useAccountContextMenu';
export const ContextMenu = React.forwardRef((_a, ref) => {
    var { items, position, onClose, minWidth, maxWidth, hasParent, openedMenuRefs } = _a, otherProps = __rest(_a, ["items", "position", "onClose", "minWidth", "maxWidth", "hasParent", "openedMenuRefs"]);
    const internalRef = useRef(null);
    const menuRef = (ref !== null && ref !== void 0 ? ref : internalRef);
    const subMenuRef = useRef(null);
    // Root menu creates the shared array; submenus receive and reuse the same instance via props.
    const internalExcludeMenuRefs = useRef([]);
    const excludeMenuRefs = openedMenuRefs !== null && openedMenuRefs !== void 0 ? openedMenuRefs : internalExcludeMenuRefs.current;
    const { subMenuState, hasLeftOffset, hasRightOffset, onItemPointerEnter } = useContextMenu({
        menuRef,
        menuProps: { items, position, onClose, minWidth, maxWidth, hasParent },
        openedMenuRefs: excludeMenuRefs
    });
    const { hasInstrumentTypeOffset } = useAccountContextMenu({ items });
    const handleMenuItemClick = useCallback((itemState, action) => {
        if (itemState.disabled) {
            return;
        }
        action === null || action === void 0 ? void 0 : action();
        if (itemState.checked === undefined) {
            onClose();
        }
    }, [onClose]);
    const renderItem = (item) => {
        switch (item.type) {
            case 'separator':
                return _jsx(ContextMenuItemSeparator, { "data-testid": item.id }, item.id);
            case 'item':
                return (_jsx(ContextMenuItem, { text: item.text, hasLeftOffset: hasLeftOffset, hasRightOffset: hasRightOffset, hasSubitems: isValidArray(item.subitems), tooltip: item.tooltip, icon: item.icon, disabled: item.disabled, disabledReason: item.disabledReason, checked: item.checked, selected: item.selected, isBeta: item.isBeta, onPointerEnter: (element) => {
                        onItemPointerEnter(item, element);
                    }, onClick: () => {
                        handleMenuItemClick(item, () => {
                            var _a;
                            (_a = item.onClick) === null || _a === void 0 ? void 0 : _a.call(item, item);
                        });
                    } }, item.id));
            case 'account':
                return (_jsx(ContextMenuItemAccount, { accountName: item.accountName, accountMode: item.accountMode, hasInstrumentTypeOffset: hasInstrumentTypeOffset, instrumentType: item.instrumentType, disabled: item.disabled, checked: item.checked, onClick: () => {
                        handleMenuItemClick(item, () => {
                            var _a;
                            (_a = item.onClick) === null || _a === void 0 ? void 0 : _a.call(item, item);
                        });
                    }, "data-testid": item.id }, item.id));
            case 'actions':
                return (_jsx(ContextMenuItemActions, { text: item.text, actions: item.actions.map((action) => (Object.assign(Object.assign({}, action), { onClick: () => {
                            handleMenuItemClick(item, () => {
                                var _a;
                                (_a = action.onClick) === null || _a === void 0 ? void 0 : _a.call(action);
                            });
                        } }))), checked: item.checked, onClick: () => {
                        handleMenuItemClick(item, () => {
                            var _a;
                            (_a = item.onClick) === null || _a === void 0 ? void 0 : _a.call(item, item);
                        });
                    }, "data-testid": item.id }, item.id));
        }
    };
    if (items.length === 0) {
        return null;
    }
    const accountItems = items.filter((item) => item.type === 'account');
    const otherItems = items.filter((item) => item.type !== 'account');
    return (_jsxs(_Fragment, { children: [_jsx(Popover, { ref: menuRef, className: "context-menu-popover", top: position.y, left: position.x, minWidth: minWidth, maxWidth: maxWidth, excludeRefs: excludeMenuRefs, onOutsideClick: onClose, children: _jsxs("ul", Object.assign({ className: "context-menu-list", role: "menu" /* NOSONAR S6842 - ul as menu with role is intentional */ }, otherProps, { children: [isValidArray(accountItems) && (_jsx("fieldset", { className: "context-menu-list-accounts", role: "group", children: accountItems.map(renderItem) })), otherItems.map(renderItem)] })) }), subMenuState !== null && isValidArray(subMenuState.items) && (_jsx(ContextMenu, { ref: subMenuRef, position: subMenuState.position, items: subMenuState.items, minWidth: minWidth, maxWidth: maxWidth, hasParent: true, openedMenuRefs: excludeMenuRefs, onClose: onClose }))] }));
});
